import {
  DownloadSimpleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PropertyManager, useGetPropertyManagers } from '@/api/property-managers'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Select } from '@/components/ui/select'
import { RowAction } from '@/components/ui/table'
import { TextInput } from '@/components/ui/text-input'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { popupFactory, toastFactory } from '@/factories'
import { useDesignVersionToggle, useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { getApiErrorMessage } from '@/utils/error'

import { BuildingFilter } from '../shared/building-filter.styled'
import { CreatedDateRangePicker } from '../shared/created-date-range-picker'
import { exportCsv } from '../shared/export-csv'
import { filterByDateRange, paginateItems } from '../shared/person-table.utils'
import { useDetailsDeepLink } from '../shared/use-details-deep-link'
import { DeletePropertyManagerConfirmationPopup } from './delete-property-manager-confirmation-popup'
import { PropertyManagerDetailsModal } from './property-manager-details-modal'
import { PropertyManagerFormModal } from './property-manager-form-modal'
import {
  getPropertyManagersTableColumns,
  PROPERTY_MANAGERS_TABLE_ID,
  propertyManagersTableFiltersSchema,
  propertyManagersTableInitialFilters,
} from './property-managers.constants'
import {
  filterPropertyManagers,
  filterPropertyManagersByBuildings,
  getPropertyManagerBuildingOptions,
  getPropertyManagersCsvFields,
  sortPropertyManagers,
} from './property-managers.utils'

export const PropertyManagersPage = () => {
  const { t } = useTranslation(['organisation'])

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'property-managers',
    screen: 'PropertyManagers',
    to: { path: '/dashboard/organisation/property-managers/legacy' },
    version: 'new',
  })

  const { data, error, isLoading } = useGetPropertyManagers()

  const searchParams = useSearchParams({
    initialState: propertyManagersTableInitialFilters,
    schema: propertyManagersTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: propertyManagersTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { buildingIds, fromDate, limit, search, skip, sortBy, sortOrder, toDate } =
    tableFilters.filters

  const [searchDraft, handleSearchChange] = useSearchDraft(search, (value) =>
    tableFilters.setFilter('search', value),
  )

  const buildingOptions = useMemo(() => getPropertyManagerBuildingOptions(data ?? []), [data])

  const filteredPropertyManagers = useMemo(
    () =>
      sortPropertyManagers(
        filterPropertyManagers(
          filterPropertyManagersByBuildings(
            filterByDateRange(
              data ?? [],
              fromDate,
              toDate,
              (propertyManager) => propertyManager.createdAt,
            ),
            buildingIds,
          ),
          search,
        ),
        sortBy,
        sortOrder,
      ),
    [data, fromDate, toDate, buildingIds, search, sortBy, sortOrder],
  )

  const pagePropertyManagers = useMemo(
    () => paginateItems(filteredPropertyManagers, skip, limit),
    [filteredPropertyManagers, skip, limit],
  )

  const handleCreate = () => {
    popupFactory.open(PropertyManagerFormModal, { mode: 'create' })
  }

  const [isExporting, setIsExporting] = useState(false)

  const handleExportCsv = async () => {
    const selectedBuildings = buildingOptions.filter((option) =>
      buildingIds?.includes(option.value),
    )

    setIsExporting(true)
    try {
      await exportCsv({
        buildingName: selectedBuildings.length === 1 ? selectedBuildings[0].label : undefined,
        data: filteredPropertyManagers,
        fields: getPropertyManagersCsvFields(t),
        page: 'property-managers',
      })
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setIsExporting(false)
    }
  }

  const handleBuildingsChange = (values: number[]) => {
    tableFilters.setFilter('buildingIds', values.length ? values : undefined)
  }

  const handleSort = (nextSortBy: string | null, nextSortOrder: 'asc' | 'desc' | null) => {
    tableFilters.setFilters({
      sortBy: (nextSortBy as typeof sortBy) ?? undefined,
      sortOrder: nextSortOrder ?? undefined,
    })
  }

  const openDetails = useDetailsDeepLink(
    PropertyManagerDetailsModal,
    (propertyManagerId: number) => ({ propertyManagerId }),
  )

  const columns = useMemo(() => getPropertyManagersTableColumns(t, openDetails), [t, openDetails])

  const rowActions: RowAction<PropertyManager>[] = useMemo(
    () => [
      {
        icon: EyeIcon,
        label: t('organisation:propertyManagers.list.rowActions.details'),
        onClick: (propertyManager) => openDetails(propertyManager.id),
        primary: true,
      },
      {
        icon: PencilSimpleIcon,
        label: t('organisation:propertyManagers.list.rowActions.edit'),
        onClick: (propertyManager) =>
          popupFactory.open(PropertyManagerFormModal, { mode: 'edit', propertyManager }),
      },
      {
        icon: TrashIcon,
        label: t('organisation:propertyManagers.list.rowActions.delete'),
        onClick: (propertyManager) =>
          popupFactory.open(DeletePropertyManagerConfirmationPopup, { propertyManager }),
        variant: 'danger',
      },
    ],
    [t, openDetails],
  )

  if (redirect) return redirect

  return (
    <Page>
      <PageHeader
        title={t('organisation:propertyManagers.list.title')}
        actions={
          <>
            <ToggleSwitch
              falsyLabel={t('organisation:propertyManagers.list.designToggle.old')}
              truthyLabel={t('organisation:propertyManagers.list.designToggle.new')}
              checked={true}
              onChange={handleUIToggle}
            />

            <Button size="small" use="primary" onClick={handleCreate}>
              {t('organisation:propertyManagers.list.createNew')}
              <Icon icon={PlusIcon} />
            </Button>
          </>
        }
      />

      <TableGroup
        columns={columns}
        data={pagePropertyManagers}
        emptyNoFilters={{
          description: t('organisation:propertyManagers.list.empty.noFilters.description'),
          title: t('organisation:propertyManagers.list.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('organisation:propertyManagers.list.empty.withFilters.description'),
          title: t('organisation:propertyManagers.list.empty.withFilters.title'),
        }}
        error={error}
        filters={
          <BuildingFilter>
            <Select
              searchable
              type="multi"
              options={buildingOptions}
              placeholder={t('organisation:propertyManagers.list.filters.buildings')}
              size="small"
              value={buildingIds ?? []}
              onChange={handleBuildingsChange}
              onReset={() => handleBuildingsChange([])}
            />
          </BuildingFilter>
        }
        getRowKey={(propertyManager) => propertyManager.id}
        id={PROPERTY_MANAGERS_TABLE_ID}
        loading={isLoading}
        navigation={{
          actions: (
            <Button
              onlyBox
              disabled={!filteredPropertyManagers.length}
              loading={isExporting}
              size="small"
              title={t('organisation:propertyManagers.list.exportCsv')}
              use="secondary"
              onClick={handleExportCsv}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <CreatedDateRangePicker
              fromDate={fromDate}
              toDate={toDate}
              onChange={(range) => tableFilters.setFilters(range)}
            />
          ),
          search: (
            <TextInput
              clearable
              icon={MagnifyingGlassIcon}
              name="search"
              placeholder={t('organisation:propertyManagers.list.search.placeholder')}
              size="small"
              value={searchDraft}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          ),
        }}
        pagination={{
          paginationFilters: tableFilters,
          total: filteredPropertyManagers.length,
        }}
        rowActions={rowActions}
        sortBy={sortBy ?? null}
        sortOrder={sortOrder ?? null}
        tableFilters={tableFilters}
        onSort={handleSort}
      />
    </Page>
  )
}
